import React, { useEffect, useState } from "react";
import Popup from "reactjs-popup";
import { FormControl, Button } from "react-bootstrap";
import { DeleteFileSvg } from "../Svg/allsvg";
import {
  listRedactionReasons,
  addRedactionReason,
  deleteRedactionReason,
  reorderRedactionReasons,
} from "../../libs/API";

export default function RedactionReasonPopup({ show, onHide, clientId }) {
  const [newReason, setNewReason] = useState("");
  const [reasons, setReasons] = useState([]);
  const [initialOrder, setInitialOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [dragIndex, setDragIndex] = useState(null);

  useEffect(() => {
    if (show) fetchReasons();
  }, [show]);

  const fetchReasons = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await listRedactionReasons(clientId);
      setReasons(data);
      setInitialOrder(data);
    } catch {
      setError("Could not load reasons.");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newReason.trim()) return;
    setLoading(true);
    setError("");
    try {
      await addRedactionReason(clientId, newReason.trim());
      setNewReason("");
      await fetchReasons();
    } catch {
      setError("Could not add reason.");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (reason) => {
    setLoading(true);
    setError("");
    try {
      await deleteRedactionReason(clientId, reason);
      await fetchReasons();
    } catch {
      setError("Could not delete reason.");
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, idx) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, idx) => {
    e.preventDefault();
    const from = dragIndex;
    const to = idx;
    if (from === null || from === to) return;
    const updated = [...reasons];
    const [moved] = updated.splice(from, 1);
    updated.splice(to, 0, moved);
    setReasons(updated);
  };

  const handleClose = async () => {
    if (JSON.stringify(initialOrder) !== JSON.stringify(reasons)) {
      setLoading(true);
      setError("");
      try {
        await reorderRedactionReasons(clientId, reasons);
      } catch {
        setError("Could not save new order.");
      } finally {
        setLoading(false);
      }
    }
    onHide();
  };

  return (
    <Popup
      open={show}
      closeOnDocumentClick={false}
      onClose={handleClose}
      contentStyle={{ width: "600px", borderRadius: "8px", padding: "20px" }}
    >
      <div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h4>Manage Redaction Reasons</h4>
          <Button
            bsStyle="link"
            onClick={handleClose}
            style={{ fontSize: "1.2em" }}
          >
            &times;
          </Button>
        </div>
        <div style={{ margin: "16px 0" }}>
          <FormControl
            type="text"
            placeholder="Enter a new reason..."
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            disabled={loading}
            style={{
              width: "80%",
              marginRight: "8px",
              display: "inline-block",
            }}
          />
          <Button bsStyle="primary" onClick={handleAdd} disabled={loading}>
            {loading ? "..." : "Add"}
          </Button>
          {error && (
            <div style={{ color: "red", marginTop: "8px" }}>{error}</div>
          )}
        </div>
        <div style={{ maxHeight: "350px", overflowY: "auto" }}>
          {reasons.length > 0 ? (
            reasons.map((reason, idx) => (
              <div
                key={idx}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, idx)}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  padding: "8px 0",
                  borderBottom: "1px solid #eee",
                  cursor: "move",
                }}
              >
                <span
                  style={{
                    flex: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {reason}
                </span>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button
                    bsStyle="danger"
                    bsSize="xsmall"
                    onClick={() => handleDelete(reason)}
                    disabled={loading}
                  >
                    <DeleteFileSvg />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: "center", color: "#777" }}>
              No reasons added yet.
            </div>
          )}
        </div>
      </div>
    </Popup>
  );
}
